import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Building2, Phone, User, Edit } from "lucide-react";

const EMPTY = { name: "", contact_person: "", phone: "", notes: "" };

export default function InstitutionsPage() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/institutions").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Kurum adı zorunludur");
    try {
      if (editing) await api.put(`/institutions/${editing.id}`, form);
      else await api.post("/institutions", form);
      toast.success(editing ? "Kurum güncellendi" : "Kurum eklendi");
      setOpen(false); setForm(EMPTY); setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const remove = async (i) => {
    if (!window.confirm(`"${i.name}" silinsin mi?`)) return;
    try {
      await api.delete(`/institutions/${i.id}`);
      toast.success("Silindi"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  return (
    <AppLayout
      title="Kurumlar"
      subtitle="Yatırım ve iş birliği yapılan kurumlar"
      actions={
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(EMPTY); } }}>
          <DialogTrigger asChild>
            <Button data-testid="new-institution-button" className="bg-emerald-700 hover:bg-emerald-800">
              <Plus className="h-4 w-4 mr-1.5" /> Yeni Kurum
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="institution-dialog">
            <DialogHeader><DialogTitle>{editing ? "Kurumu Düzenle" : "Yeni Kurum"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Kurum Adı *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       data-testid="institution-name-input" />
              </div>
              <div>
                <Label>İlgili Kişi</Label>
                <Input value={form.contact_person}
                       onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
              </div>
              <div>
                <Label>Telefon</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <Label>Notlar</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
              <Button onClick={create} className="bg-emerald-700 hover:bg-emerald-800"
                      data-testid="institution-submit-button">{editing ? "Güncelle" : "Ekle"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((i) => (
          <Card key={i.id} className="p-5 border border-slate-200 shadow-sm" data-testid={`institution-card-${i.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="h-9 w-9 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center">
                  <Building2 className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900">{i.name}</div>
                  {i.contact_person && (
                    <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                      <User className="h-3 w-3" /> {i.contact_person}
                    </div>
                  )}
                  {i.phone && (
                    <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                      <Phone className="h-3 w-3" /> {i.phone}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex">
                <Button variant="ghost" size="icon" data-testid={`edit-institution-${i.id}`}
                        onClick={() => { setEditing(i); setForm({ name: i.name, contact_person: i.contact_person || "", phone: i.phone || "", notes: i.notes || "" }); setOpen(true); }}>
                  <Edit className="h-4 w-4" />
                </Button>
                {isAdmin && (
                  <Button variant="ghost" size="icon" onClick={() => remove(i)}
                          data-testid={`delete-institution-${i.id}`}>
                    <Trash2 className="h-4 w-4 text-rose-600" />
                  </Button>
                )}
              </div>
            </div>
            {i.notes && <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600">{i.notes}</div>}
          </Card>
        ))}
        {items.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-500">Kurum eklenmedi.</div>
        )}
      </div>
    </AppLayout>
  );
}
